import React, { useCallback, useEffect, useRef, useState } from "react";

import BankReconciliation from "./BankReconciliation";
import ExportButton from "./ExportButton";
import "./ReportsPage.css";

const API_URL = process.env.REACT_APP_API_URL || "";

// No router: the four reports are tabs in local state, like the worklist's filter.
export const TABS = [
  { key: "ar-closing", label: "Weekly A/R closing" },
  { key: "deposits", label: "Deposits" },
  { key: "mtd-collections", label: "MTD collections" },
  { key: "mismatched-postings", label: "Mismatched postings" },
];

// Local calendar date as YYYY-MM-DD. Built from parts, never toISOString(), which converts
// to UTC and lands a day early west of Greenwich in the evening.
export function isoDay(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function money(value) {
  if (value === null || value === undefined) return "—";
  return Number(value).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

// Each tab's query string, from the tab's own inputs. The table fetch and the CSV export
// both call this, so the file is the table.
// A date input passes through half-typed values ("0002-09-15") while a year is being typed.
// Only a plausible full date is sent, so typing does not flash the server's 400.
export function isCompleteDate(value) {
  return /^(19|20)\d{2}-\d{2}-\d{2}$/.test(value || "");
}

export function inputsComplete(tab, inputs) {
  if (tab === "ar-closing") return isCompleteDate(inputs.asOf);
  if (tab === "mtd-collections") return isCompleteDate(inputs.reportDate);
  return isCompleteDate(inputs.dateFrom) && isCompleteDate(inputs.dateTo);
}

export function reportParams(tab, inputs) {
  const params = new URLSearchParams();
  if (tab === "ar-closing" && inputs.asOf) params.set("as_of", inputs.asOf);
  if (tab === "mtd-collections" && inputs.reportDate)
    params.set("report_date", inputs.reportDate);
  if (tab === "deposits" || tab === "mismatched-postings") {
    if (inputs.dateFrom) params.set("date_from", inputs.dateFrom);
    if (inputs.dateTo) params.set("date_to", inputs.dateTo);
  }
  return params;
}

function defaultInputs() {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    asOf: isoDay(today),
    reportDate: isoDay(today),
    dateFrom: isoDay(first),
    dateTo: isoDay(today),
  };
}

function ArClosing({ data }) {
  const labels = data.buckets;
  const section = (side, title) => {
    const current = data.current[side];
    const prior = data.prior[side];
    const priorByPayer = Object.fromEntries(
      prior.by_payer.map((r) => [r.payer_name, r]),
    );
    return (
      <div className="reports-card">
        <h3>{title}</h3>
        <table className="reports-table">
          <thead>
            <tr>
              <th>{side === "insurance" ? "Payer" : ""}</th>
              {labels.map((l) => (
                <th key={l} className="num">
                  {l}
                </th>
              ))}
              <th className="num">Total {data.current.as_of}</th>
              <th
                className="num reports-prior"
                title="Today's balances aged as of a week earlier, without claims serviced since. Atlas cannot rebuild last week's balances."
              >
                Aged to {data.prior.as_of}
              </th>
            </tr>
          </thead>
          <tbody>
            {side === "insurance" &&
              current.by_payer.map((row) => (
                <tr key={row.payer_name}>
                  <td>{row.payer_name}</td>
                  {labels.map((l) => (
                    <td key={l} className="num">
                      {money(row.buckets[l])}
                    </td>
                  ))}
                  <td className="num">{money(row.total)}</td>
                  <td className="num reports-prior">
                    {money(priorByPayer[row.payer_name]?.total ?? 0)}
                  </td>
                </tr>
              ))}
            <tr className="reports-total-row">
              <td>Total</td>
              {labels.map((l) => (
                <td key={l} className="num">
                  {money(current.totals[l])}
                </td>
              ))}
              <td className="num">{money(current.total)}</td>
              <td className="num reports-prior">{money(prior.total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };
  return (
    <>
      {section("insurance", "Insurance A/R")}
      {section("patient", "Patient A/R")}
      <p className="reports-note">
        {data.definitions.insurance} {data.definitions.as_of}
      </p>
    </>
  );
}

function Deposits({ data }) {
  if (!data.deposits.length) {
    return (
      <p className="reports-empty">No posted ERA deposits in this range.</p>
    );
  }
  return (
    <div className="reports-card">
      <table className="reports-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Payer</th>
            <th>Check / trace</th>
            <th>Method</th>
            <th className="num">Amount</th>
            <th className="num">Claims paid</th>
          </tr>
        </thead>
        <tbody>
          {data.deposits.map((d) => (
            <tr key={d.remittance_id}>
              <td>{d.deposit_date}</td>
              <td>{d.payer_name}</td>
              <td>{d.check_number || "—"}</td>
              <td>{d.payment_method || "—"}</td>
              <td className="num">{money(d.amount)}</td>
              <td className="num">{d.claims.length}</td>
            </tr>
          ))}
          <tr className="reports-total-row">
            <td colSpan={4}>Total</td>
            <td className="num">{money(data.total)}</td>
            <td />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function MtdCollections({ data }) {
  return (
    <>
      <div className="reports-stats">
        <div className="reports-stat">
          <span>
            Collected {data.window.start} to {data.window.end}
          </span>
          <strong>{money(data.total)}</strong>
        </div>
        <div className="reports-stat">
          <span>Insurance</span>
          <strong>{money(data.insurance)}</strong>
        </div>
        <div className="reports-stat">
          <span>Patient</span>
          <strong>{money(data.patient)}</strong>
        </div>
      </div>
      <p className="reports-note">
        The same month-to-date figure as the morning text message and Mission Control.
      </p>
      {data.total === null && (
        <p className="reports-empty">No money was collected in this period.</p>
      )}
      {data.undated_money > 0 && (
        <p className="reports-note">
          {money(data.undated_money)} of this practice's collected money has no
          payment date anywhere in the ledger, so it is counted in no period.
        </p>
      )}
      {data.by_payer.length > 0 && (
        <div className="reports-grid">
          <div className="reports-card">
            <h3>By payer</h3>
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Payer</th>
                  <th className="num">Insurance</th>
                  <th className="num">Patient</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.by_payer.map((r) => (
                  <tr key={r.payer_name}>
                    <td>{r.payer_name}</td>
                    <td className="num">{money(r.insurance)}</td>
                    <td className="num">{money(r.patient)}</td>
                    <td className="num">{money(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="reports-card">
            <h3>By day</h3>
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="num">Collected</th>
                </tr>
              </thead>
              <tbody>
                {data.by_day.map((r) => (
                  <tr key={r.date}>
                    <td>{r.date}</td>
                    <td className="num">{money(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

const KIND_LABELS = {
  billed_total: "Billed total differs",
  lines: "Procedure codes differ",
};

function Mismatched({ data }) {
  return (
    <>
      <div className="reports-card">
        <h3>
          Posted to a claim that disagrees with the remit (
          {data.mismatched.length})
        </h3>
        {data.mismatched.length === 0 ? (
          <p className="reports-empty">None in this range.</p>
        ) : (
          <table className="reports-table">
            <thead>
              <tr>
                <th>Remit date</th>
                <th>Payer</th>
                <th>Claim</th>
                <th>DOS</th>
                <th>What differs</th>
                <th>Claim</th>
                <th>Remit</th>
              </tr>
            </thead>
            <tbody>
              {data.mismatched.map((r) => (
                <tr key={`${r.remittance_id}-${r.claim_id}-${r.kind}`}>
                  <td>{r.deposit_date}</td>
                  <td>{r.payer_name}</td>
                  <td>#{r.claim_id}</td>
                  <td>{r.date_of_service}</td>
                  <td>{KIND_LABELS[r.kind] || r.kind}</td>
                  <td>
                    {r.kind === "lines"
                      ? r.claim_codes.join(", ")
                      : money(r.claim_billed)}
                  </td>
                  <td>
                    {r.kind === "lines"
                      ? r.remit_codes.join(", ")
                      : money(r.remit_billed)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="reports-card">
        <h3>Remits with no matching claim ({data.unmatched.length})</h3>
        {data.unmatched.length === 0 ? (
          <p className="reports-empty">None in this range.</p>
        ) : (
          <table className="reports-table">
            <thead>
              <tr>
                <th>Remit date</th>
                <th>Payer</th>
                <th>Patient (as the payer printed it)</th>
                <th>Service date</th>
                <th>Control number</th>
                <th className="num">Billed</th>
                <th className="num">Paid</th>
              </tr>
            </thead>
            <tbody>
              {data.unmatched.map((r) => (
                <tr key={`${r.remittance_id}-${r.parsed_claim_index}`}>
                  <td>{r.deposit_date}</td>
                  <td>{r.payer_name}</td>
                  <td>{r.patient_name || "—"}</td>
                  <td>{r.service_date || "—"}</td>
                  <td>{r.patient_control_number || "—"}</td>
                  <td className="num">{money(r.remit_billed)}</td>
                  <td className="num">{money(r.remit_paid)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

export default function ReportsPage({
  selectedPractice,
  features = {},
  readOnly = false,
}) {
  const practiceId = selectedPractice?.id;
  const [tab, setTab] = useState("ar-closing");
  const [inputs, setInputs] = useState(defaultInputs);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const ticket = useRef(0);

  const load = useCallback(async () => {
    if (!practiceId || !inputsComplete(tab, inputs)) return;
    const mine = ++ticket.current;
    setLoading(true);
    setError(null);
    try {
      const params = reportParams(tab, inputs);
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/reports/${tab}${params.toString() ? `?${params}` : ""}`,
        { credentials: "include" },
      );
      const body = await response.json().catch(() => null);
      if (mine !== ticket.current) return;
      if (!response.ok || !body) {
        setData(null);
        setError((body && body.error) || "Could not load this report.");
        return;
      }
      setData({ tab, body });
    } catch (err) {
      if (mine === ticket.current) setError("Could not load this report.");
    } finally {
      if (mine === ticket.current) setLoading(false);
    }
  }, [practiceId, tab, inputs]);

  useEffect(() => {
    load();
  }, [load]);

  // A practice switch re-renders this page with a new prop; nothing of the old practice's
  // report may paint under the new name.
  useEffect(() => {
    setData(null);
    setError(null);
  }, [practiceId]);

  if (!practiceId) {
    return (
      <div className="reports-page">
        <p className="reports-empty">Select a practice to see its reports.</p>
      </div>
    );
  }

  const set = (key) => (e) =>
    setInputs((prev) => ({ ...prev, [key]: e.target.value }));
  const shown = data && data.tab === tab ? data.body : null;

  return (
    <div className="reports-page">
      <div className="reports-header">
        <div>
          <h1>Reports</h1>
          <p className="reports-sub">{selectedPractice.name}</p>
        </div>
      </div>

      <div className="reports-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={`reports-tab ${tab === t.key ? "active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="reports-controls">
        {tab === "ar-closing" && (
          <label>
            As of{" "}
            <input type="date" value={inputs.asOf} onChange={set("asOf")} />
          </label>
        )}
        {tab === "mtd-collections" && (
          <label>
            Month to date on{" "}
            <input
              type="date"
              value={inputs.reportDate}
              onChange={set("reportDate")}
            />
          </label>
        )}
        {(tab === "deposits" || tab === "mismatched-postings") && (
          <>
            <label>
              From{" "}
              <input
                type="date"
                value={inputs.dateFrom}
                onChange={set("dateFrom")}
              />
            </label>
            <label>
              To{" "}
              <input
                type="date"
                value={inputs.dateTo}
                onChange={set("dateTo")}
              />
            </label>
          </>
        )}
        <button
          className="btn btn-secondary btn-sm"
          onClick={load}
          disabled={loading}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
        {features.exports && (
          <ExportButton
            practiceId={practiceId}
            path={`reports/${tab}`}
            kind={`report-${tab}`}
            getParams={() => reportParams(tab, inputs)}
            readOnly={readOnly}
          />
        )}
      </div>

      {error && (
        <div className="reports-message error" role="alert">
          <span>{error}</span>
          <button
            className="message-close"
            aria-label="Dismiss"
            onClick={() => setError(null)}
          >
            &times;
          </button>
        </div>
      )}

      {loading && !shown && <p className="reports-empty">Loading…</p>}
      {shown && tab === "ar-closing" && <ArClosing data={shown} />}
      {shown && tab === "deposits" && <Deposits data={shown} />}
      {tab === "deposits" && inputsComplete(tab, inputs) && (
        <BankReconciliation
          practiceId={practiceId}
          dateFrom={inputs.dateFrom}
          dateTo={inputs.dateTo}
          readOnly={readOnly}
        />
      )}
      {shown && tab === "mtd-collections" && <MtdCollections data={shown} />}
      {shown && tab === "mismatched-postings" && <Mismatched data={shown} />}
    </div>
  );
}
